import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

interface InvoiceLineItemsProps {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
  currency?: string;
}

export function InvoiceLineItems({ items, onChange, currency = 'ZAR' }: InvoiceLineItemsProps) {
  const handleAddItem = () => {
    onChange([...items, { description: '', quantity: 1, unitPrice: 0 }]);
  };

  const handleRemoveItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, description: value } : item,
    );
    onChange(updated);
  };

  const handleQuantityChange = (index: number, value: string) => {
    const quantity = parseInt(value, 10) || 0;
    const updated = items.map((item, i) =>
      i === index ? { ...item, quantity: Math.max(0, quantity) } : item,
    );
    onChange(updated);
  };

  const handleUnitPriceChange = (index: number, value: string) => {
    const unitPrice = parseFloat(value) || 0;
    const updated = items.map((item, i) =>
      i === index ? { ...item, unitPrice: Math.max(0, unitPrice) } : item,
    );
    onChange(updated);
  };

  const calculateRowTotal = (item: LineItem): number => {
    return item.quantity * item.unitPrice;
  };

  const subtotal = items.reduce((sum, item) => sum + calculateRowTotal(item), 0);
  const vat = subtotal * 0.15;
  const total = subtotal + vat;

  return (
    <div className="space-y-3">
      {items.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead className="w-[100px]">Qty</TableHead>
              <TableHead className="w-[140px]">Unit Price</TableHead>
              <TableHead className="w-[140px] text-right">Total</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Input
                    placeholder="Description of service or product"
                    value={item.description}
                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={item.quantity || ''}
                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                    placeholder="1"
                  />
                </TableCell>
                <TableCell>
                  {/*
                    step must stay at cent granularity. step="100" made the
                    browser reject any amount that wasn't a multiple of R100 —
                    Nicole's real first invoice (R23 040) could not be created.
                  */}
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice || ''}
                    onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                    placeholder="0.00"
                  />
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(calculateRowTotal(item), currency)}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-text-tertiary hover:text-red-500"
                    onClick={() => handleRemoveItem(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="text-right font-medium">
                Subtotal
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(subtotal, currency)}
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} className="text-right font-medium">
                VAT (15%)
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(vat, currency)}
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} className="text-right font-semibold text-lg">
                Total
              </TableCell>
              <TableCell className="text-right font-semibold text-lg text-emerald-500">
                {formatCurrency(total, currency)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      ) : (
        <div className="flex flex-col items-center justify-center py-6 text-text-tertiary border border-dashed border-white/10 rounded-lg">
          <p className="text-sm">No line items added</p>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAddItem}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Line Item
      </Button>
    </div>
  );
}
